import '../locales/i18n';
import 'simplebar/src/simplebar.css';
import 'react-image-lightbox/style.css';
import 'mapbox-gl/dist/mapbox-gl.css';
import 'react-quill/dist/quill.snow.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import 'react-lazy-load-image-component/src/effects/blur.css';
import 'react-lazy-load-image-component/src/effects/opacity.css';
import 'react-lazy-load-image-component/src/effects/black-and-white.css';
import cookie from 'cookie';
import { ReactElement, ReactNode, useEffect } from 'react';
import { NextPage } from 'next';
import Head from 'next/head';
import App, { AppProps, AppContext } from 'next/app';
import { Provider as ReduxProvider } from 'react-redux';
import { PersistGate } from 'redux-persist/lib/integration/react';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import * as Sentry from '@sentry/nextjs';
import { store, persistor } from '../redux/store';
import { getSettings } from '../utils/settings';
import { SettingsValueProps } from '../components/settings/type';
import { SettingsProvider } from '../contexts/SettingsContext';
import { CollapseDrawerProvider } from '../contexts/CollapseDrawerContext';
import ThemeProvider from '../theme';
import { ChartStyle } from '../components/chart';
import RtlLayout from '../components/RtlLayout';
import ProgressBar from '../components/ProgressBar';
import ThemeColorPresets from '../components/ThemeColorPresets';
import NotistackProvider from '../components/NotistackProvider';
import ThemeLocalization from '../components/ThemeLocalization';
import MotionLazyContainer from '../components/animate/MotionLazyContainer';
import { AuthProvider } from '../contexts/AwsAuthContext';
import ErrorBoundary from '../components/ErrorBoundary';
import { ApolloProvider } from '@apollo/client';
import { client } from 'src/utils/apolloClient';
import ja from 'date-fns/locale/ja';
import { ErrorProvider } from 'src/contexts/ErrorContext';
import { ChannelIO } from '../modules/ChannelTalk';
import ProvideMSGraphContext from 'src/contexts/MSGraphContext';
import { BrowserRouter } from 'react-router-dom';
import { GoogleTagManager } from '@next/third-parties/google';
import Script from 'next/script';
import { CybozuAuthProvider } from '../contexts/CybozuAuthContext';
import { JSTProvider } from '../contexts/JSTContext';
import { OrganizationProvider } from '../contexts/OrganizationContext';
import { useRouter } from 'next/router';

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode;
};

interface MyAppProps extends AppProps {
  settings: SettingsValueProps;
  Component: NextPageWithLayout;
}

// 新規追加: Organization用のラッパーコンポーネント
function ApplicationWrapper({ children }: { children: ReactNode }) {
  console.log('OrganizationWrapper');
  const router = useRouter();
  const isOrganizationPage = router.pathname.startsWith('/app/[organizationName]');
  const isGlobalOrganizationPage = router.pathname.startsWith(
    '/global/organizations/[organizationID]'
  );

  // 動的パラメータの抽出ロジック
  let organizationName = router.query.organizationName as string;
  let organizationId = router.query.organizationID as string;

  // organizationNameがプレースホルダーの場合、実際のパスから抽出
  if (organizationName === '[organizationName]' && router.asPath) {
    const pathSegments = router.asPath.split('/');
    // /app/[organizationName] の構造を想定して、2番目のセグメント（index 2）が組織名
    if (pathSegments.length > 2 && pathSegments[1] === 'app') {
      organizationName = pathSegments[2];
      // クエリパラメータが含まれている場合は除去
      organizationName = organizationName.split('?')[0];
    }
  }

  // organizationIDがプレースホルダーの場合、実際のパスから抽出
  if (organizationId === '[organizationID]' && router.asPath) {
    const pathSegments = router.asPath.split('/');
    // /global/organizations/[organizationID] の構造を想定して、3番目のセグメント（index 3）が組織ID
    if (
      pathSegments.length > 3 &&
      pathSegments[1] === 'global' &&
      pathSegments[2] === 'organizations'
    ) {
      organizationId = pathSegments[3];
      // クエリパラメータが含まれている場合は除去
      organizationId = organizationId.split('?')[0];
    }
  }

  console.log('isOrganizationPage', isOrganizationPage);
  console.log('isGlobalOrganizationPage', isGlobalOrganizationPage);
  console.log('organizationName', organizationName);
  console.log('organizationId', organizationId);
  console.log('router.isReady', router.isReady);
  console.log('router.asPath', router.asPath);

  // 組織ページの場合（/app/[organizationName]）
  if (isOrganizationPage) {
    // ルーターが準備完了していない場合、またはorganizationNameが取得できない場合
    if (!router.isReady || !organizationName || organizationName === '[organizationName]') {
      return (
        <div className="flex justify-center items-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
          <span className="ml-3 text-gray-600">ページを読み込み中...</span>
        </div>
      );
    }

    // 有効なorganizationNameが取得できた場合
    return (
      <OrganizationProvider organizationName={organizationName}>
        <CybozuAuthProvider>{children}</CybozuAuthProvider>
      </OrganizationProvider>
    );
  }

  // グローバル組織ページの場合（/global/organizations/[organizationID]）
  if (isGlobalOrganizationPage) {
    // ルーターが準備完了していない場合、またはorganizationIdが取得できない場合
    if (!router.isReady || !organizationId || organizationId === '[organizationID]') {
      return (
        <div className="flex justify-center items-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
          <span className="ml-3 text-gray-600">ページを読み込み中...</span>
        </div>
      );
    }

    // 有効なorganizationIdが取得できた場合
    return (
      <OrganizationProvider organizationId={organizationId} variant="global">
        <CybozuAuthProvider>{children}</CybozuAuthProvider>
      </OrganizationProvider>
    );
  }

  // 組織ページ以外はそのまま
  return <>{children}</>;
}

export default function MyApp(props: MyAppProps) {
  const { Component, pageProps, settings } = props;
  const getLayout = Component.getLayout ?? ((page) => page);

  // Sentry 初期化（クライアントサイドのみ）
  useEffect(() => {
    Sentry.init({
      dsn:
        process.env.NEXT_PUBLIC_SENTRY_DSN ||
        'https://a4f486b24dc44b718365cf7d4981a095@o470149.ingest.us.sentry.io/5500411',
      environment: process.env.NEXT_PUBLIC_BACKEND_NAME || 'development',
      tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,
      enableLogs: true,
      debug: process.env.NODE_ENV === 'development',
      integrations: [],
      profilesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,
      beforeSend(event, hint) {
        const error = hint.originalException;
        if (error && typeof error === 'object' && 'message' in error) {
          const message = error.message as string;
          if (
            message.includes('ECONNRESET') ||
            message.includes('EPIPE') ||
            message.includes('Client disconnected') ||
            message.includes('socket hang up') ||
            message.includes('ENOTFOUND') ||
            message.includes('ETIMEDOUT')
          ) {
            return null;
          }
        }
        event.tags = {
          ...event.tags,
          component: 'client',
        };
        return event;
      },
      beforeSendTransaction(event) {
        if (
          event.transaction === '/health' ||
          event.transaction === '/api/health' ||
          event.transaction === '/monitoring'
        ) {
          return null;
        }
        return event;
      },
    });
  }, []);

  const gtmId = process.env.NEXT_PUBLIC_GTM_ID;

  return (
    <ErrorBoundary showDetails={process.env.NODE_ENV === 'development'}>
      <Script
        id="user-guiding"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
                console.log('[UserGuiding] Initializing...');
                (function(g,u,i,d,e,s){
                  g[e]=g[e]||[];
                  var f=u.getElementsByTagName(i)[0];
                  var k=u.createElement(i);
                  k.async=true;
                  k.src='https://static.userguiding.com/media/user-guiding-'+s+'-embedded.js';
                  k.onload = function() {
                    console.log('[UserGuiding] External script loaded successfully');
                    console.log('[UserGuiding] window.userGuiding:', g[d]);
                  };
                  k.onerror = function(e) {
                    console.error('[UserGuiding] Failed to load external script:', e);
                  };
                  f.parentNode.insertBefore(k,f);
                  console.log('[UserGuiding] Script element inserted, src:', k.src);
                  if(g[d]) {
                    console.log('[UserGuiding] userGuiding already exists, skipping initialization');
                    return;
                  }
                  var ug=g[d]={q:[]};
                  ug.c=function(n){return function(){ug.q.push([n,arguments])};};
                  var m=['previewGuide','finishPreview','track','identify','hideChecklist','launchChecklist'];
                  for(var j=0;j<m.length;j++){ug[m[j]]=ug.c(m[j]);}
                  console.log('[UserGuiding] Stub object created:', g[d]);
                })(window,document,'script','userGuiding','userGuidingLayer','KAX9863467ZID');
              `,
        }}
      />
      <Head>
        <meta name="viewport" content="initial-scale=1, width=device-width" />
      </Head>
      <GoogleTagManager gtmId={gtmId as string} />
      <AuthProvider>
        <ApolloProvider client={client}>
          <ReduxProvider store={store}>
            <PersistGate loading={null} persistor={persistor}>
              <CollapseDrawerProvider>
                <SettingsProvider defaultSettings={settings}>
                  <ThemeProvider>
                    <NotistackProvider>
                      <ErrorProvider>
                        <ProvideMSGraphContext>
                          <ApplicationWrapper>
                            <JSTProvider>
                              <MotionLazyContainer>
                                <ThemeColorPresets>
                                  <ThemeLocalization>
                                    <LocalizationProvider
                                      dateAdapter={AdapterDateFns}
                                      dateFormats={{ normalDate: 'YYYY/mm/dd' }}
                                      locale={ja}
                                    >
                                      <RtlLayout>
                                        <BrowserRouter>
                                          <ChartStyle />
                                          <ProgressBar />
                                          {getLayout(<Component {...pageProps} />)}
                                          <ChannelIO />
                                        </BrowserRouter>
                                      </RtlLayout>
                                    </LocalizationProvider>
                                  </ThemeLocalization>
                                </ThemeColorPresets>
                              </MotionLazyContainer>
                            </JSTProvider>
                          </ApplicationWrapper>
                        </ProvideMSGraphContext>
                      </ErrorProvider>
                    </NotistackProvider>
                  </ThemeProvider>
                </SettingsProvider>
              </CollapseDrawerProvider>
            </PersistGate>
          </ReduxProvider>
        </ApolloProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

MyApp.getInitialProps = async (context: AppContext) => {
  const appProps = await App.getInitialProps(context);

  const cookies = cookie.parse(
    context.ctx.req ? context.ctx.req.headers.cookie || '' : document.cookie
  );

  const settings = getSettings(cookies);

  return {
    ...appProps,
    settings,
  };
};
